import { decodeTargetUnit } from "decoders/commitment";
import { TargetUnit } from "./commitment";
import { ReportStatus } from "./contractorReport";
import { getDayDiff } from "@utils/date";

export type SocialCommitmentAnalysisLineItem = {
  id: string;
  targetType: string;
  targetDescription?: string;
  targetValue: number;
  achievedValue: number;
  prevAchievedValue: number;
};

export type ProjectAnalysisLineItem = {
  projectId: string;
  projectName: string;
  projectStartDate: Date;
  projectEndDate: Date;
  contractorId: string;
  contractorLogo: string;
  contractorName: string;
  totalSpendTarget: number;
  totalSpendAchieved: number;

  totalHoursTarget: number;
  totalHoursAchieved: number;
  targets: SocialCommitmentAnalysisLineItem[];
  packages: PackageAnalysisLineItem[];
};

export type PackageAnalysisLineItem = {
  contractorPackageId: string;
  packageStartDate: Date;
  packageEndDate: Date;
  contractorId: string;
  contractorLogo: string;
  contractorName: string;

  reportStatus: string;
  targets: SocialCommitmentAnalysisLineItem[];
  reportData: ReportAnalysisLineItem[];

  totalSpendTarget: number;
  totalSpendAchieved: number;

  totalHoursTarget: number;
  totalHoursAchieved: number;
};

export interface Aggregate {
  allocated: number;
  achieved: number;
  target: number;
  pcChange: number;
}

export type ProjectAnalysisAggregateLineItem = {
  reportYear: number;
  reportMonth: number;
  reportId: string;
  projectId: string;

  contractorId: string;
  contractorLogo: string;
  contractorName: string;

  totalSpend: Aggregate;
  totalHours: Aggregate;
};

export type PackageAnalysisAggregateLineItem = {
  reportYear: number;
  reportMonth: number;
  reportId: string;
  reportStatus: ReportStatus;
  contractorId: string;
  contractorLogo: string;
  contractorName: string;
  packageId: string;

  totalSpend: Aggregate;

  totalHours: Aggregate;
};

export interface ReportAnalysisLineItem {
  reportId: string;
  reportStatus: ReportStatus;
  reportDateMonth: number;
  reportDateYear: number;

  totalSpendTarget: number;
  totalSpendAchieved: number;

  totalHoursTarget: number;
  totalHoursAchieved: number;

  targets: SocialCommitmentAnalysisLineItem[];

  spendData: AnalysisByteSpendLineItem[];
  hourData: AnalysisByteHourLineItem[];
}

export interface AnalysisByteSpendLineItem {
  contractorReportId: string;
  socialProcurementId: string;
  targetIds: string[];
  value: number;
  prevValue: number;
}

export interface AnalysisByteHourLineItem {
  contractorReportId: string;
  contractorEmployeeId: string;
  targetIds: string[];
  value: number;
  prevValue: number;
}

export interface LineChartItem {
  x: Date;
  y: number;
  prevValue: number;
  target: number;
  targetId: string;
  targetType: string;
  targetDescription?: string;
  borderDash?: number[];
  borderWidth?: number;
  borderColor?: string;
  pointBorderColor?: string;
  pointBackgroundColor?: string;
}

const colours = [
  "#90CAF9",
  "#2196F3",
  "#1565C0",
  "#69F0AE",
  "#F44336",
  "#FFAB91",
  "#FFE57F",
];

interface YearMonthPair {
  year: number;
  month: number;
}

export const getLineChartData = (
  projectAnalysis: ProjectAnalysisLineItem
): LineChartItem[][] => {
  const lineChartData: LineChartItem[][] = [];

  const reportDates = new Set<YearMonthPair>();
  projectAnalysis.packages.forEach((p) => {
    p.reportData.forEach((r) => {
      reportDates.add({ year: r.reportDateYear, month: r.reportDateMonth });
    });
  });

  [projectAnalysis.targets[0]].forEach((target) => {
    const lineChartAchievedData: LineChartItem[] = [
      {
        x: new Date(
          projectAnalysis.projectStartDate.getFullYear(),
          projectAnalysis.projectStartDate.getMonth(),
          0
        ),
        y: 0,
        prevValue: 0,
        targetId: target.id,
        target: target.targetValue,
        targetDescription: target.targetDescription,
      } as LineChartItem,
    ];

    const lineChartTargetData: LineChartItem[] = [
      {
        x: new Date(
          projectAnalysis.projectStartDate.getFullYear(),
          projectAnalysis.projectStartDate.getMonth(),
          0
        ),
        y: 0,
        borderDash: [5, 5],
        prevValue: 0,
        targetId: target.id,
        target: 0,
        targetDescription: target.targetDescription,
      } as LineChartItem,
    ];

    Array.from(reportDates).forEach((r, index) => {
      const currentDate = new Date(r.year, r.month, 0);
      let prevReportDate = projectAnalysis.projectStartDate;
      if (index > 0) {
        prevReportDate = new Date(r.year, currentDate.getMonth(), 0);
      }
      //Get number of days in report
      const daysInReportMonth = getDayDiff(prevReportDate, currentDate);
      const totalDaysInProject = getDayDiff(
        projectAnalysis.projectStartDate,
        projectAnalysis.projectEndDate
      );

      const totalTargetSpend =
        (projectAnalysis.totalSpendTarget / totalDaysInProject) *
        daysInReportMonth;
      const totalTargetHours =
        (projectAnalysis.totalHoursTarget / totalDaysInProject) *
        daysInReportMonth;

      const lineChartItem = {
        x: new Date(r.year, r.month, 0),
        y: 0,
        prevValue: 0,
        targetId: target.id,
        target: target.targetValue,
        targetDescription: target.targetDescription,
        borderWidth: 1,
        borderColor: "#922893",
        pointBorderColor: "#922893",
        pointBackgroundColor: "transparent",
      } as LineChartItem;

      const lineTargetChartItem = {
        x: new Date(r.year, r.month + 1, 0),
        y: 0,
        prevValue: 0,
        targetId: target.id,
        target: 0,
        targetDescription: target.targetDescription,
        borderDash: [5, 5],
      } as LineChartItem;

      projectAnalysis.packages.forEach((p) => {
        //TODO: Fix the off by one date issue
        const report = p.reportData.find(
          (pr) => pr.reportDateYear === r.year && pr.reportDateMonth === r.month
        );
        if (report) {
          const reportTarget = report.targets.find((t) => t.id === target.id);
          if (reportTarget) {
            lineChartItem.y = lineChartItem.y + reportTarget.achievedValue;
            lineChartItem.prevValue =
              lineChartItem.prevValue + reportTarget.prevAchievedValue;

            lineTargetChartItem.y =
              lineTargetChartItem.y + reportTarget.targetValue;
          }
        }
      });

      lineChartItem.y = lineChartItem.y + lineChartItem.prevValue;

      lineTargetChartItem.y =
        lineTargetChartItem.y +
        lineChartTargetData.reduce((acc, l) => {
          return acc + l.y;
        }, 0);

      if (decodeTargetUnit(target.targetType) === TargetUnit.Spend) {
        lineChartItem.y = (lineChartItem.y / totalTargetSpend) * 100;
        lineChartItem.prevValue = (lineChartItem.y / totalTargetSpend) * 100;

        // lineTargetChartItem.y =
        //   (lineTargetChartItem.y / totalTargetSpend) * 100;
      } else {
        lineChartItem.y = (lineChartItem.y / totalTargetHours) * 100;
        lineChartItem.prevValue = (lineChartItem.y / totalTargetHours) * 100;

        // lineTargetChartItem.y =
        //   (lineTargetChartItem.y / totalTargetHours) * 100;
      }

      lineChartAchievedData.push(lineChartItem);
      lineChartTargetData.push(lineTargetChartItem);
    });
    // lineChartData.push(lineChartAchievedData);
    lineChartData.push(lineChartTargetData);
  });

  return lineChartData;
};

const createDefaultProjectAnalysisAggregateLineItem = (
  project: ProjectAnalysisLineItem,
  year: number,
  month: number,
  reportId: string
): ProjectAnalysisAggregateLineItem => {
  return {
    reportYear: year,
    reportMonth: month,
    reportId,
    projectId: project.projectId,
    contractorId: project.contractorId,
    contractorName: project.contractorName,
    contractorLogo: project.contractorLogo,

    totalSpend: {
      allocated: 0,
      achieved: 0,
      target: 0,
      pcChange: 0,
    } as Aggregate,

    totalHours: {
      allocated: 0,
      achieved: 0,
      target: 0,
      pcChange: 0,
    } as Aggregate,
  };
};

const createDefaultPackageAnalysisAggregateLineItem = (
  contractorPackage: PackageAnalysisLineItem,
  year: number,
  month: number,
  reportId: string
): PackageAnalysisAggregateLineItem => {
  return {
    reportYear: year,
    reportMonth: month,
    reportId,
    reportStatus: ReportStatus.Upcoming,
    packageId: contractorPackage.contractorPackageId,
    contractorId: contractorPackage.contractorId,
    contractorName: contractorPackage.contractorName,
    contractorLogo: contractorPackage.contractorLogo,

    totalSpend: {
      allocated: 0,
      achieved: 0,
      target: 0,
      pcChange: 0,
    } as Aggregate,

    totalHours: {
      allocated: 0,
      achieved: 0,
      target: 0,
      pcChange: 0,
    } as Aggregate,
  };
};

const createDefaultReportAnalysisLineItem = (): ReportAnalysisLineItem => {
  return {
    reportId: "",
    reportStatus: ReportStatus.Upcoming,
    reportDateMonth: 0,
    reportDateYear: 0,

    totalSpendTarget: 0,
    totalSpendAchieved: 0,

    totalHoursTarget: 0,
    totalHoursAchieved: 0,
    targets: [],
    spendData: [],
    hourData: [],
  };
};

const getPcChange = (a: number, b: number) => {
  let percent;
  if (b !== 0) {
    if (a !== 0) {
      percent = ((b - a) / a) * 100;
    } else {
      percent = b * 100;
    }
  } else {
    percent = -a * 100;
  }
  return Math.floor(percent);
};

export const getAnalysisAcrossMonth = (
  analysis: ProjectAnalysisLineItem,
  month: number,
  year: number
): ProjectAnalysisAggregateLineItem | undefined => {
  const allAggregatedData: ProjectAnalysisAggregateLineItem =
    createDefaultProjectAnalysisAggregateLineItem(analysis, year, month, "");

  for (const p of analysis.packages) {
    let allReports = p.reportData;
    allReports.sort(
      (r1: ReportAnalysisLineItem, r2: ReportAnalysisLineItem) => {
        if (r1.reportDateYear < r2.reportDateYear) {
          return -1;
        }
        if (r1.reportDateYear > r2.reportDateYear) {
          return 1;
        }
        if (r1.reportDateMonth < r2.reportDateMonth) {
          return -1;
        }
        if (r1.reportDateMonth > r2.reportDateMonth) {
          return 1;
        }
        return 0;
      }
    );
    allReports = allReports.filter((d) => {
      return (
        d.reportDateMonth === month &&
        d.reportDateYear === year &&
        d.reportDateMonth !== 0
      );
    });

    if (allReports.length > 0) {
      allReports.forEach((report, index) => {
        const prevReport =
          index === 0
            ? createDefaultReportAnalysisLineItem()
            : allReports[index - 1];

        report.targets.forEach((target) => {
          const prevTarget = prevReport.targets.find((t) => t.id === target.id);
        });

        //Add up all the data for the reports
      });
    }
  }
  return allAggregatedData;
};

export const getPackageAnalysisAcrossMonth = (
  analysis: ProjectAnalysisLineItem,
  packageId: string,
  month: number,
  year: number
): ProjectAnalysisAggregateLineItem | undefined => {
  const allAggregatedData: ProjectAnalysisAggregateLineItem =
    createDefaultProjectAnalysisAggregateLineItem(analysis, year, month, "");
  const packageAnalysis = analysis.packages.filter(
    (p) => p.contractorPackageId === packageId
  );
  for (const p of packageAnalysis) {
    let allReports = p.reportData;
    allReports.sort(
      (r1: ReportAnalysisLineItem, r2: ReportAnalysisLineItem) => {
        if (r1.reportDateYear < r2.reportDateYear) {
          return -1;
        }
        if (r1.reportDateYear > r2.reportDateYear) {
          return 1;
        }
        if (r1.reportDateMonth < r2.reportDateMonth) {
          return -1;
        }
        if (r1.reportDateMonth > r2.reportDateMonth) {
          return 1;
        }
        return 0;
      }
    );
    allReports = allReports.filter((d) => {
      return (
        d.reportDateMonth === month &&
        d.reportDateYear === year &&
        d.reportDateMonth !== 0
      );
    });

    if (allReports.length > 0) {
      allReports.forEach((report) => {
        //Add up all the data for the reports
      });
    }
  }
  return allAggregatedData;
};

export const getReportForMonth = (
  analysis: ProjectAnalysisLineItem,
  packageId: string,
  month: number,
  year: number
): ReportAnalysisLineItem[] => {
  const packageAnalysis = analysis.packages.find(
    (p) => p.contractorPackageId === packageId
  );
  if (packageAnalysis) {
    let allReports = packageAnalysis.reportData;
    allReports.sort(
      (r1: ReportAnalysisLineItem, r2: ReportAnalysisLineItem) => {
        if (r1.reportDateYear < r2.reportDateYear) {
          return -1;
        }
        if (r1.reportDateYear > r2.reportDateYear) {
          return 1;
        }
        if (r1.reportDateMonth < r2.reportDateMonth) {
          return -1;
        }
        if (r1.reportDateMonth > r2.reportDateMonth) {
          return 1;
        }
        return 0;
      }
    );
    allReports = allReports.filter((d) => {
      return (
        d.reportDateMonth <= month &&
        d.reportDateYear <= year &&
        d.reportDateMonth !== 0
      );
    });
    return allReports;
  }
  return [];
};

export const getAnalysisPackageAcrossMonth = (
  analysis: ProjectAnalysisLineItem,
  month: number,
  year: number
): PackageAnalysisAggregateLineItem[] | undefined => {
  const allAggregatedData: PackageAnalysisAggregateLineItem[] = [];

  for (const p of analysis.packages) {
    const aggregatedData = createDefaultPackageAnalysisAggregateLineItem(
      p,
      year,
      month,
      ""
    );
    const allReports = p.reportData;
    allReports.sort(
      (r1: ReportAnalysisLineItem, r2: ReportAnalysisLineItem) => {
        if (r1.reportDateYear < r2.reportDateYear) {
          return -1;
        }
        if (r1.reportDateYear > r2.reportDateYear) {
          return 1;
        }
        if (r1.reportDateMonth < r2.reportDateMonth) {
          return -1;
        }
        if (r1.reportDateMonth > r2.reportDateMonth) {
          return 1;
        }
        return 0;
      }
    );
    const monthReports = allReports.filter((d) => {
      return (
        d.reportDateMonth === month &&
        d.reportDateYear === year &&
        d.reportDateMonth !== 0
      );
    });

    if (monthReports.length > 0) {
      monthReports.forEach((report) => {
        const reportIndex = allReports.indexOf(report);
        let prevReport = createDefaultReportAnalysisLineItem();
        if (reportIndex > 0) {
          prevReport = allReports[reportIndex - 1];
        }

        //Add up all the report data
      });
      allAggregatedData.push(aggregatedData);
    }
  }
  return allAggregatedData;
};

export interface SeverityRow {
  label: string;
  severity: "error" | "warning" | "success";
  total: number;
  totalTarget: number;
  pcChange: number;
  ratio: number;
}
